import { Link } from "react-router-dom";
import { Calculator, FileText, Table2, Clock3, ArrowRight, CheckCircle2, ShieldCheck, Sparkles, Utensils, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import PromotionalAd from "../components/PromotionalAd";

const HERO_IMG = "https://images.pexels.com/photos/35671829/pexels-photo-35671829.jpeg";
const CLASSROOM_IMG = "https://images.pexels.com/photos/35152622/pexels-photo-35152622.jpeg";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="bg-poshan-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="chip chip-saffron" data-testid="hero-badge">
              <Sparkles size={14}/> PM POSHAN Daily Calculator · Lifetime rates 2012–2100
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              MDM cooking cost, <span className="text-emerald-700">solved in seconds.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
              A free, no-login calculator for HMs, teachers, MDM in-charges and CRCCs. Enter
              attendance for Balvatika, Primary, Upper Primary and High School — instantly get
              the day’s menu, rice, dal, vegetables, oil, laddoo and cost, with PDF and Excel export.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white h-12 px-6 text-base" data-testid="hero-cta-start">
                <Link to="/calculator">Open Calculator <ArrowRight size={18}/></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-6 text-base border-slate-300" data-testid="hero-cta-about">
                <Link to="/about">Learn about PM POSHAN</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> No signup, no login</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> Works on low-end Android</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700"/> Data never leaves your device</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="School children eating mid-day meal"
                className="rounded-2xl shadow-[0_20px_60px_rgba(4,120,87,0.18)] w-full h-[380px] object-cover border border-white"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border border-slate-200 shadow-lg p-4 w-56 hidden sm:block">
                <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Today</div>
                <div className="mt-1 font-display font-extrabold text-2xl text-slate-900">₹ 1,247.20</div>
                <div className="text-xs text-slate-500">147 primary · 63 upper</div>
              </div>
              <div className="absolute -top-4 -right-4 bg-emerald-700 text-white rounded-xl px-4 py-3 shadow-lg hidden sm:block">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Rice needed</div>
                <div className="font-display font-extrabold text-xl">24.15 kg</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="features-section">
        <div className="text-center max-w-2xl mx-auto">
          <div className="chip chip-green"><ShieldCheck size={14}/> Everything you need</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">One page. Every ingredient. Every rate.</h2>
          <p className="mt-3 text-slate-600">Built to be finished in under a minute, even on a 2G Android in a village school.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <FeatureCard testid="feature-calc" icon={<Calculator className="text-emerald-700" size={22}/>} title="Auto-calculated quantities" body="Rice, dal, soya, oil, vegetables, laddoo, condiments — the calculator applies the right per-child grams and rates for the date." tint="emerald"/>
          <FeatureCard testid="feature-menu" icon={<Utensils className="text-orange-600" size={22}/>} title="Weekday menu built-in" body="Egg day, Dalma day, Soya day, Laddoo day — the menu changes automatically with the weekday." tint="orange"/>
          <FeatureCard testid="feature-exports" icon={<FileText className="text-emerald-700" size={22}/>} title="PDF &amp; Excel export" body="One tap to download a clean printable PDF for the MDM register or an Excel sheet for monthly reports." tint="emerald"/>
        </div>
      </section>

      {/* Sponsored — one rotating placement (weighted rotation is session-scoped) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4" aria-labelledby="promo-network-heading" data-testid="landing-promo-network">
        <div className="text-center">
          <div className="chip chip-saffron inline-flex items-center gap-1"><Sparkles size={12}/> Sponsored · Teacher Productivity Network</div>
          <h2 id="promo-network-heading" className="mt-3 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">A recommended tool for Odisha teachers</h2>
          <p className="mt-2 text-slate-600 max-w-2xl mx-auto">This calculator handles PM Poshan. Our sister platforms handle the rest of the paperwork — lesson plans, everyday utilities and MDM record-keeping.</p>
        </div>
        <div className="mt-8" data-testid="landing-promo-single">
          <PromotionalAd placement="homepage" variant="horizontal"/>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-slate-200 py-16" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <img src={CLASSROOM_IMG} alt="Teacher and children" className="rounded-2xl w-full h-[380px] object-cover shadow-lg border border-slate-100"/>
          </div>
          <div className="lg:col-span-7">
            <div className="chip chip-saffron"><Clock3 size={14}/> How it works</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Five steps. Under a minute.</h2>
            <ol className="mt-8 space-y-6">
              <Step n={1} title="Pick the date"    body="Any day between 2012 and 2100. The tool reads the weekday and shows the day’s menu — including whether a laddoo is served."/>
              <Step n={2} title="Enter attendance" body="Steppers for Balvatika, Primary (I–V), Upper Primary (VI–VIII) and High School (IX–X). Count only children who actually ate."/>
              <Step n={3} title="Generate report"  body="One click. Get the class-wise ingredient breakdown, aggregated totals, grand total, average per student and total rice."/>
              <Step n={4} title="Review totals"    body="KPIs at the top; class-wise cards below. Vegetables auto-balance to the statutory per-student total from May 2025."/>
              <Step n={5} title="Export"           body="Download a clean PDF for your MDM register or an Excel workbook with a sheet per category for monthly reporting."/>
            </ol>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="benefits-section">
        <div className="grid md:grid-cols-4 gap-6">
          <Benefit num="4" label="Student categories supported"/>
          <Benefit num="88" label="Years of rates (2012–2100)"/>
          <Benefit num="⚖" label="Auto-balanced vegetables"/>
          <Benefit num="₹0" label="Free. No ads, no login."/>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-emerald-700 text-white p-8 sm:p-12 relative overflow-hidden" data-testid="landing-final-cta">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-orange-500/30 blur-3xl"/>
          <div className="absolute -left-10 -bottom-10 h-52 w-52 rounded-full bg-emerald-400/30 blur-3xl"/>
          <div className="relative grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-orange-200"><Users size={16}/> Built for teachers</div>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold">Skip the arithmetic. Get back to the classroom.</h3>
              <p className="mt-3 text-emerald-50/90 max-w-xl">Open the calculator now — set up takes zero seconds and you can be done with today’s ingredient list in under a minute.</p>
            </div>
            <div className="flex lg:justify-end">
              <Button asChild size="lg" className="bg-white text-emerald-800 hover:bg-orange-50 h-12 px-6 text-base font-bold" data-testid="cta-open-app">
                <Link to="/calculator">Open PM POSHAN Calculator <ArrowRight size={18}/></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon, title, body, tint, testid }) {
  const bg = tint === "emerald" ? "bg-emerald-50" : "bg-orange-50";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow fade-rise" data-testid={testid}>
      <div className={`h-11 w-11 rounded-lg ${bg} grid place-items-center`}>{icon}</div>
      <h3 className="mt-4 font-display font-bold text-lg text-slate-900" dangerouslySetInnerHTML={{ __html: title }}/>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: body }}/>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="h-10 w-10 shrink-0 rounded-lg bg-emerald-700 text-white grid place-items-center font-bold shadow-sm">{n}</div>
      <div>
        <div className="font-display font-bold text-lg text-slate-900">{title}</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function Benefit({ num, label }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
      <div className="font-display font-extrabold text-3xl text-emerald-700">{num}</div>
      <div className="mt-1 text-sm text-slate-600 font-medium">{label}</div>
    </div>
  );
}
